import { IconButton as MuiIconButton, SxProps, Theme } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { forwardRef, type ReactNode } from 'react';

export interface CloseButtonProps {
  /**
   * Custom icon to render instead of the default Close icon.
   */
  icon?: ReactNode;

  /**
   * The color of the close icon.
   * @default 'default'
   */
  color?:
    | 'inherit'
    | 'default'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning';

  /**
   * If true, the button will be disabled.
   * @default false
   */
  disabled?: boolean;

  /**
   * Custom styles applied to the root element.
   */
  sx?: SxProps<Theme>;

  /**
   * Callback fired when the button is clicked.
   */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;

  /**
   * The aria-label attribute for accessibility.
   * @default 'Close'
   */
  'aria-label'?: string;

  /**
   * The data-testid attribute for testing.
   */
  'data-testid'?: string;

  /**
   * CSS class name applied to the root element.
   */
  className?: string;
}

const CLOSE_BUTTON_SIZE = 25;
const CLOSE_ICON_SIZE = 16;

/**
 * CloseButton Component
 *
 * A standardized 25x25 transparent close button for dismissing modals,
 * sidebars, popovers, and other dismissible UI elements.
 *
 * @example
 * ```tsx
 * <CloseButton onClick={handleClose} />
 * ```
 *
 * @example
 * ```tsx
 * // With custom aria-label for i18n
 * <CloseButton onClick={handleClose} aria-label={translate('map.close')} />
 * ```
 */
export const CloseButton = forwardRef<HTMLButtonElement, CloseButtonProps>(
  (
    {
      icon,
      color = 'default',
      disabled = false,
      sx,
      onClick,
      'aria-label': ariaLabel = 'Close',
      'data-testid': dataTestId,
      className,
      ...props
    },
    ref
  ) => {
    const baseStyles: SxProps<Theme> = {
      width: CLOSE_BUTTON_SIZE,
      height: CLOSE_BUTTON_SIZE,
      minWidth: CLOSE_BUTTON_SIZE,
      padding: 0,
    };

    return (
      <MuiIconButton
        ref={ref}
        className={className}
        color={color}
        disabled={disabled}
        onClick={onClick}
        aria-label={ariaLabel}
        data-testid={dataTestId}
        sx={[baseStyles, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
        {...props}
      >
        {icon ?? <CloseIcon sx={{ fontSize: CLOSE_ICON_SIZE }} />}
      </MuiIconButton>
    );
  }
);

CloseButton.displayName = 'CloseButton';
